import { useLayoutEffect, useRef, useState } from "react";
import { autoScrollSpeed, nearestSlot, shiftFor } from "../core/drag";

/** The drag in progress: the item's index, the slot it would drop into, and each item's vertical shift in px. */
export interface DragState {
  from: number;
  to: number;
  /** translateY for item k: the dragged one follows the finger, the ones in between make room */
  shift(k: number): number;
}

interface Tracking {
  from: number;
  to: number;
  clientY: number;
  centers: number[];
  /** the dragged item's height plus the gap to its neighbour */
  size: number;
  /** finger minus the item's centre when grabbed, so the item does not jump if the page cannot scroll far enough */
  grab: number;
  /** set until the collapsed list has been measured */
  measuring: boolean;
  frame: number;
}

/**
 * Drag-and-drop reordering of a vertical list by a handle, for touch and mouse (pointer events with capture, the
 * handle needs `touch-action: none`). The caller collapses the items while `drag` is set; once they are collapsed
 * the slots are measured and the page is scrolled so the dragged item sits under the finger. The list scrolls by
 * itself near the screen edges. `onDrop` gets the move when the finger is lifted.
 */
export function useDragReorder(count: number, onDrop: (from: number, to: number) => void) {
  const [drag, setDrag] = useState<DragState>();
  const items = useRef<(HTMLElement | null)[]>([]);
  const tracking = useRef<Tracking>(undefined);

  const update = () => {
    const s = tracking.current;
    if (!s || s.measuring) return;
    const y = s.clientY + window.scrollY - s.grab;
    const { from, size } = s;
    const to = (s.to = nearestSlot(y, s.centers));
    const offset = y - s.centers[from];
    setDrag({ from, to, shift: (k) => (k === from ? offset : shiftFor(k, from, to, size)) });
  };
  const autoScroll = () => {
    const s = tracking.current;
    if (!s) return;
    const speed = autoScrollSpeed(s.clientY, window.innerHeight);
    if (speed) {
      window.scrollBy(0, speed);
      update();
    }
    s.frame = requestAnimationFrame(autoScroll);
  };

  useLayoutEffect(() => {
    const s = tracking.current;
    if (!drag || !s?.measuring) return;
    const el = items.current[s.from];
    if (el) {
      const r = el.getBoundingClientRect();
      window.scrollBy(0, r.top + r.height / 2 - s.clientY);
    }
    const rects = items.current.slice(0, count).map((x) => x?.getBoundingClientRect() ?? new DOMRect());
    s.centers = rects.map((r) => r.top + window.scrollY + r.height / 2);
    const gap = rects.length > 1 ? rects[1].top - rects[0].bottom : 0;
    s.size = rects[s.from].height + gap;
    s.grab = s.clientY + window.scrollY - s.centers[s.from];
    s.measuring = false;
    s.frame = requestAnimationFrame(autoScroll);
    update();
  });

  const end = (commit: boolean) => {
    const s = tracking.current;
    if (!s) return;
    cancelAnimationFrame(s.frame);
    tracking.current = undefined;
    setDrag(undefined);
    if (commit && s.from !== s.to) onDrop(s.from, s.to);
  };

  /** Props for the drag handle of item i. */
  const handle = (i: number) => ({
    onPointerDown: (e: React.PointerEvent<HTMLElement>) => {
      if (tracking.current || (e.pointerType === "mouse" && e.button !== 0)) return;
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      tracking.current = { from: i, to: i, clientY: e.clientY, centers: [], size: 0, grab: 0, measuring: true, frame: 0 };
      setDrag({ from: i, to: i, shift: () => 0 });
    },
    onPointerMove: (e: React.PointerEvent<HTMLElement>) => {
      if (!tracking.current) return;
      tracking.current.clientY = e.clientY;
      update();
    },
    onPointerUp: () => end(true),
    onPointerCancel: () => end(false),
  });

  /** Ref callback for item i (the element whose position is measured). */
  const item = (i: number) => (el: HTMLElement | null) => { items.current[i] = el; };

  return { drag, handle, item };
}
